'use client'

/**
 * §19.2 / §19.10 — "one structure, two views, not two stacks."
 *
 * A single append-only session log backs both the activity rail (§19.2,
 * unfiltered, newest first, with timestamps) and the undo stack (§19.10,
 * filtered to undoable entries, without timestamps, capped at 20).
 *
 * The rail is NOT a second live region. §11.1 designates lib/announce.ts
 * the site's one live-region writer, with its shell nodes mounted once in
 * app/layout.tsx. So "homeless" entries (generation failed, credit
 * refunded, undo discarding a redo branch, work restored) call the shared
 * announce() rather than the rail declaring its own aria-live node —
 * otherwise two announcements could collide, which is exactly what §11.1
 * exists to prevent. Element-owned entries (a trim, a fade, a gain change)
 * are still appended to the log — so the rail carries a record of them —
 * but nothing announces on their behalf; the owning control already did or
 * the change is silent (session start, autosave).
 *
 * CORRECTION (final adversarial review): this file previously relied on
 * activity-rail.tsx's list rendering with role="log" and no aria-live
 * attribute to keep the rail non-announcing. role="log" carries an
 * IMPLICIT aria-live="polite" per the ARIA spec — confirmed against
 * Chrome's own accessibility tree, which reported {"live":"polite"} on
 * that node regardless of the missing attribute. So every entry appended
 * below was already being announced once by the rail, and the "homeless"
 * entries below were announced a SECOND time via announce() on the
 * assertive channel — the two collided in the same tick. Fixed by
 * rendering the rail's list as role="list" (no implicit live behaviour)
 * instead of role="log"; announce() below is now genuinely the only
 * speaker for these entries.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { announce } from '@/lib/announce'

export type SessionLogKind =
  | 'file'
  | 'trim'
  | 'fade'
  | 'gain'
  | 'normalize'
  | 'export'
  | 'credit'
  | 'generation'
  | 'undo'
  | 'redo'
  | 'session'

export interface SessionLogEntry {
  id: string
  t: number
  kind: SessionLogKind
  label: string
  undoable: boolean
}

/** What the rail persists across a refresh — labels only, never callbacks. */
interface StoredEntry {
  id: string
  t: number
  kind: SessionLogKind
  label: string
  undoable: boolean
}

const STORAGE_KEY = 'tls-create-session-log'
const RAIL_CAP = 200
const UNDO_CAP = 20

interface UndoRecord {
  entryId: string
  label: string
  undo: () => void
  redo: () => void
}

let seq = 0
function nextId(): string {
  seq += 1
  return `sl-${Date.now()}-${seq}`
}

function loadStored(): StoredEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persist(entries: StoredEntry[]): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-RAIL_CAP)))
  } catch {
    // sessionStorage can throw in private-browsing/quota edge cases — the
    // rail still works in-memory for the rest of the session, it just
    // won't survive a refresh. Not worth surfacing to the user.
  }
}

export interface AddEntryOptions {
  /** Only undoable entries feed the undo stack. */
  undoable?: boolean
  undo?: () => void
  redo?: () => void
  /** Fire the shared announce() for this entry — reserved for homeless status. */
  announce?: 'polite' | 'assertive'
  /** Marks this call as itself an undo/redo replay, so it doesn't discard the redo branch. */
  isReplay?: boolean
}

export function useSessionLog() {
  // The server cannot see sessionStorage. Start from the same empty snapshot
  // on both sides of hydration, then restore the prior session in an effect.
  // Reading storage in the lazy initializer made the first client render say
  // “Session started” while the server still said “No activity yet”, which
  // forced React to discard the subtree on the voice route.
  const [log, setLog] = useState<SessionLogEntry[]>([])
  const storageHydratedRef = useRef(false)
  // Undo/redo bookkeeping lives outside the persisted log (callbacks cannot
  // be serialized), addressed by entry id so it could still be
  // cross-referenced against the log after a refresh (where it starts
  // empty, since §19.10.1 undo is disabled — not hidden — until the audio
  // re-attaches). Kept as real state, not a ref, so render can read it.
  const [undoStack, setUndoStack] = useState<UndoRecord[]>([])
  const [redoDepth, setRedoDepth] = useState(0)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- restore the client-only session snapshot after the SSR-safe initial render.
    setLog(loadStored().map(({ id, t, kind, label, undoable }) => ({ id, t, kind, label, undoable })))
    storageHydratedRef.current = true
  }, [])

  useEffect(() => {
    if (!storageHydratedRef.current) return
    persist(log.map(({ id, t, kind, label, undoable }) => ({ id, t, kind, label, undoable })))
  }, [log])

  const addEntry = useCallback((kind: SessionLogKind, label: string, opts: AddEntryOptions = {}) => {
    const entry: SessionLogEntry = {
      id: nextId(),
      t: Date.now(),
      kind,
      label,
      undoable: Boolean(opts.undoable),
    }

    // A genuinely new edit after an undo discards the redo branch (§19.10:
    // "a new edit after an undo discards them and says so in the rail").
    setRedoDepth(prevDepth => {
      if (opts.isReplay || prevDepth === 0) return prevDepth
      const discarded = prevDepth
      setUndoStack(prevStack => prevStack.slice(0, prevStack.length - discarded))
      setLog(prevLog => [...prevLog, {
        id: nextId(),
        t: Date.now(),
        kind: 'undo' as const,
        label: `Redo discarded (${discarded} step${discarded === 1 ? '' : 's'})`,
        undoable: false,
      }].slice(-RAIL_CAP))
      announce(`Redo discarded, ${discarded} step${discarded === 1 ? '' : 's'}.`, 'polite')
      return 0
    })

    if (opts.undoable && opts.undo && opts.redo) {
      setUndoStack(prev => [...prev, {
        entryId: entry.id,
        label,
        undo: opts.undo as () => void,
        redo: opts.redo as () => void,
      }].slice(-UNDO_CAP))
    }

    setLog(prev => [...prev, entry].slice(-RAIL_CAP))

    if (opts.announce) {
      announce(label, opts.announce)
    }

    return entry.id
  }, [])

  const canUndo = undoStack.length - redoDepth > 0
  const canRedo = redoDepth > 0

  const undo = useCallback(() => {
    const target = undoStack[undoStack.length - 1 - redoDepth]
    if (!target) return
    target.undo()
    setRedoDepth(d => d + 1)
    addEntry('undo', `Undo: ${target.label}`, { isReplay: true })
  }, [undoStack, redoDepth, addEntry])

  const redo = useCallback(() => {
    if (redoDepth <= 0) return
    const target = undoStack[undoStack.length - redoDepth]
    if (!target) return
    target.redo()
    setRedoDepth(d => d - 1)
    addEntry('redo', `Redo: ${target.label}`, { isReplay: true })
  }, [undoStack, redoDepth, addEntry])

  const clear = useCallback(() => {
    setUndoStack([])
    setRedoDepth(0)
    setLog([])
    if (typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    }
  }, [])

  return {
    log,
    addEntry,
    undoStack,
    redoDepth,
    canUndo,
    canRedo,
    undo,
    redo,
    clear,
  }
}

export type UseSessionLog = ReturnType<typeof useSessionLog>
